// Source : web/wp-content/themes/studiometa/src/js/components/TitleReveal.js
// Enregistré dans app.js via : 'h1, h2, h3': TitleReveal

import { Base } from '@studiometa/js-toolkit';
import { gsap, SplitText } from '../utils/gsap.js';

/**
 * Révélation des titres « à la fluid.glass ».
 *
 * Chaque titre est découpé en lignes/mots via SplitText. Les lignes sont
 * masquées (`overflow: clip`), puis les mots glissent vers le haut avec un léger
 * flou et un fondu, en cascade. L'animation se déclenche une seule fois quand le
 * titre entre dans le viewport (via ScrollTrigger).
 *
 * Accessibilité : SplitText `aria: 'auto'` conserve le texte lisible par les
 * lecteurs d'écran. En `prefers-reduced-motion: reduce`, aucun découpage ni
 * animation n'est appliqué, le titre reste affiché tel quel.
 * @augments {Base}
 */
export default class TitleReveal extends Base {
  /**
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'TitleReveal',
  };

  /**
   * @type {import('gsap').MatchMedia | null}
   */
  mm = null;

  /**
   * On masque le titre dès le montage pour éviter tout flash de contenu non
   * animé pendant le chargement des polices (le découpage SplitText n'a lieu
   * qu'une fois `document.fonts.ready` résolu).
   */
  mounted() {
    this.mm = gsap.matchMedia();

    this.mm.add(
      {
        motion: '(prefers-reduced-motion: no-preference)',
        reduced: '(prefers-reduced-motion: reduce)',
        isMobile: '(max-width: 47.999rem)',
      },
      (context) => {
        // Mouvement réduit : on s'assure simplement que le titre est visible.
        if (context.conditions?.reduced) {
          gsap.set(this.$el, { autoAlpha: 1 });
          return;
        }

        // Sur mobile, certains titres délèguent leur apparition à une cascade
        // CSS pilotée par le bloc parent (ex. le Hero, voir
        // `.block-hero-reveal-mobile` dans animate.css). Dans ce cas, on rend le
        // titre visible sans le découper : c'est le wrapper qui anime. Le desktop
        // garde le découpage SplitText habituel.
        if (context.conditions?.isMobile && this.$el.closest('[data-title-reveal-mobile-off]')) {
          gsap.set(this.$el, { autoAlpha: 1 });
          return;
        }

        gsap.set(this.$el, { autoAlpha: 0 });

        // Découpe après le chargement des polices pour des retours à la ligne
        // corrects ; `autoSplit` re-découpe ensuite si la largeur change.
        document.fonts.ready.then(() => {
          if (this.$el.isConnected === false) {
            return;
          }

          SplitText.create(this.$el, {
            type: 'lines, words',
            mask: 'lines',
            linesClass: 'title-reveal-line',
            autoSplit: true,
            aria: 'auto',
            onSplit: (self) => {
              gsap.set(this.$el, { autoAlpha: 1 });

              return gsap.from(self.words, {
                yPercent: 110,
                autoAlpha: 0,
                filter: 'blur(8px)',
                duration: 1.1,
                ease: 'power3.out',
                stagger: 0.06,
                scrollTrigger: {
                  trigger: this.$el,
                  start: 'top 85%',
                  once: true,
                },
              });
            },
          });
        });
      },
    );
  }

  /**
   * Nettoyage : `matchMedia().revert()` détruit les SplitText, ScrollTriggers et
   * tweens créés dans les callbacks ci-dessus.
   */
  destroyed() {
    this.mm?.revert();
    this.mm = null;
  }
}
